import api from '@/lib/api';

export interface DailyPickupQuery {
  date?: string | undefined;
  to?: string | undefined;
}

export interface MonthlyRevenueQuery {
  year?: number | undefined;
  month?: number | undefined;
}

export interface ChannelPerformanceQuery {
  from?: string | undefined;
  to?: string | undefined;
}

export interface PropertyPerformanceQuery {
  propertyId?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

export async function fetchDailyPickup(query?: DailyPickupQuery) {
  const { data } = await api.get('/reports/daily-pickup', { params: query });
  return data;
}

export async function fetchMonthlyRevenue(query?: MonthlyRevenueQuery) {
  const { data } = await api.get('/reports/monthly-revenue', { params: query });
  return data;
}

export async function fetchChannelPerformance(query?: ChannelPerformanceQuery) {
  const { data } = await api.get('/reports/channel-performance', { params: query });
  return data;
}

export async function fetchPropertyPerformance(query?: PropertyPerformanceQuery) {
  const { data } = await api.get('/reports/property-performance', { params: query });
  return data;
}

// ─── Reports Library & Statistics (HotelKey feature parity) ──────────────────

export type ReportCategory =
  | 'enterprise_audit'
  | 'revenue_occupancy'
  | 'housekeeping_maintenance'
  | 'reservations'
  | 'activity_booking'
  | 'companies'
  | 'downtime';

export type ReportType =
  | 'occupancy_revenue'
  | 'room_availability'
  | 'housekeeping_summary'
  | 'maintenance_activity'
  | 'booked_reservations'
  | 'pending_departures'
  | 'arrivals_list'
  | 'departures_list'
  | 'guest_balance_due'
  | 'folio_balance'
  | 'room_inventory'
  | 'room_move_log'
  | 'adjustments_log'
  | 'refunds_report';

export interface ReportDefinition {
  id: string;
  name: string;
  category: ReportCategory;
  reportType: ReportType;
  config: Record<string, unknown>;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateReportPayload {
  reportType: ReportType;
  propertyId: string;
  from: string;
  to: string;
  format?: 'json' | 'csv' | 'pdf' | undefined;
  filters?: Record<string, unknown> | undefined;
}

/**
 * Backend /reports/generate envelope. `data` is report-type specific — it may
 * be an object of scalar metrics, an object containing a nested list, or an
 * `{ error }` object for an unimplemented type. The UI normalizes it.
 */
export interface ReportResult {
  reportType: ReportType | string;
  propertyId?: string;
  from: string;
  to: string;
  generatedAt: string;
  data: unknown;
}

export interface StatisticsQuery {
  propertyId: string;
  from: string;
  to: string;
}

export interface StatisticsResponse {
  revenue: number;
  occupancyRate: number;
  adr: number;
  revPar: number;
  totalRoomNights: number;
  occupiedRoomNights: number;
}

export async function fetchReportsLibrary(): Promise<ReportDefinition[]> {
  const { data } = await api.get<ReportDefinition[]>('/reports/library');
  return data;
}

export async function generateReport(payload: GenerateReportPayload): Promise<ReportResult> {
  const { data } = await api.post('/reports/generate', payload);
  return data;
}

export async function fetchStatistics(query: StatisticsQuery): Promise<StatisticsResponse> {
  const { data } = await api.get('/reports/statistics', { params: query });
  return data;
}

// ─── Report Exports ──────────────────────────────────────────────────────────

export interface ReportExportQuery {
  from: string;
  to: string;
  propertyId: string;
  format: 'csv' | 'json';
}

export function getReportExportUrl(reportType: string, query: ReportExportQuery): string {
  const params = new URLSearchParams({ ...query });
  return `/reports/export/${reportType}?${params.toString()}`;
}

export async function downloadReportExport(
  reportType: string,
  query: ReportExportQuery,
): Promise<void> {
  const response = await api.get(`/reports/export/${reportType}`, {
    params: { ...query },
    responseType: 'blob',
  });
  const blob = new Blob([response.data as BlobPart]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${reportType}_${query.from}_${query.to}.${query.format}`;
  a.click();
  URL.revokeObjectURL(url);
}
